import { useState, useMemo } from 'react';

import { AxiosResponse } from 'axios';
import { ClassUsageReportItem } from 'types';

function useOrdering({ data }: { data: AxiosResponse<ClassUsageReportItem[]> | undefined }): {
  ordering: keyof ClassUsageReportItem;
  ascending: boolean;
  sortedData: ClassUsageReportItem[];
  handleHeaderClick: (header: keyof ClassUsageReportItem) => void;
} {
  const [ordering, setOrdering] = useState<keyof ClassUsageReportItem>('classTypeName');
  const [ascending, setAscending] = useState(true);

  const sortedData = useMemo(() => {
    if (!data?.data || !ordering) {
      return data?.data ?? [];
    }

    const dataToSort = [...data.data];

    return dataToSort.sort((a, b) => {
      const aValue = a[ordering];
      const bValue = b[ordering];

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return ascending ? aValue - bValue : bValue - aValue;
      }

      const comparison = String(aValue).localeCompare(String(bValue));
      return ascending ? comparison : -comparison;
    });
  }, [data?.data, ordering, ascending]);

  const handleHeaderClick = (header: keyof ClassUsageReportItem): void => {
    if (header === ordering) {
      setAscending(prev => !prev);
    } else {
      setOrdering(header);
      setAscending(true);
    }
  };

  return { ordering, ascending, handleHeaderClick, sortedData };
}

export default useOrdering;
